import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';

import { TranslocoPipe } from '@jsverse/transloco';

import { IconComponent } from '@shared/components';
import { EllipsisTooltipDirective } from '@shared/directives/tooltip';

import { TenantLinkDirective } from '../../../core/tenancy';
import { isRouteActive, stripTenantSegment } from '../models/navigation.model';

import type { NavigationItem, NavigationRootItem } from '../models/navigation.model';

/**
 * Navigation item component.
 * Renders a single navigation link with icon and label.
 * Supports both nested items and root-level items.
 * In collapsed mode, renders as icon-only button with tooltip.
 */
@Component({
  selector: 'app-nav-item',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TenantLinkDirective, IconComponent, TranslocoPipe, EllipsisTooltipDirective],
  host: {
    class: 'block',
  },
  template: `
    @if (collapsed()) {
      <!-- Collapsed mode: icon-only button with tooltip -->
      <a
        #tooltipTrigger
        [appTenantLink]="relativeRoute()"
        class="nav-section-trigger nav-tooltip justify-center"
        [attr.data-active]="isActive()"
        [attr.aria-current]="isActive() ? 'page' : null"
        [attr.aria-label]="item().label | transloco"
        [attr.data-tooltip]="item().label | transloco"
        (mouseenter)="showTooltip(tooltipTrigger)"
        (mouseleave)="hideTooltip(tooltipTrigger)"
      >
        <app-icon [name]="item().icon" />
      </a>
    } @else {
      <!-- Expanded mode: full item -->
      <a
        [appTenantLink]="relativeRoute()"
        class="nav-item"
        [attr.data-active]="isActive()"
        [attr.aria-current]="isActive() ? 'page' : null"
      >
        <app-icon [name]="item().icon" />
        <span class="truncate" appEllipsisTooltip [contrasted]="true">{{ item().label | transloco }}</span>
      </a>
    }
  `,
})
export class NavItemComponent {
  /** Navigation item data */
  readonly item = input.required<NavigationItem | NavigationRootItem>();

  /** Current URL for active state detection */
  readonly currentUrl = input.required<string>();

  /** Whether sidebar is collapsed (for root items) */
  readonly collapsed = input<boolean>(false);

  /** Tenant-relative route (no leading slash) fed to `[tenantLink]`. */
  protected readonly relativeRoute = computed(() => {
    const route = this.item().route;
    return route.startsWith('/') ? route.slice(1) : route;
  });

  /**
   * Whether this item is currently active. The URL now carries a `/{tenant}`
   * prefix; strip it before comparing against the tenant-relative item route.
   */
  protected readonly isActive = computed(() =>
    isRouteActive(this.item().route, stripTenantSegment(this.currentUrl()))
  );

  /** Position and show the fixed tooltip */
  protected showTooltip(el: HTMLElement): void {
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--tt-x', `${rect.right + 8}px`);
    el.style.setProperty('--tt-y', `${rect.top + rect.height / 2}px`);
    el.setAttribute('data-tooltip-visible', 'true');
  }

  /** Hide the tooltip */
  protected hideTooltip(el: HTMLElement): void {
    el.setAttribute('data-tooltip-visible', 'false');
  }
}
